import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	TemplateRef,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgxSpinnerService } from "ngx-spinner";
import { ProfileService } from "src/app/services/profile.service";
import { StorageService } from "src/app/services/storage.service";
import { environment } from "src/environments/environment";
import {
	AUTH_FORM_DROP_DOWN_STYLE,
	ERRORS,
	ROUTES,
	TAB_SET_STYLE,
	USER_TYPE,
} from "../../../constants";
import { AuthService } from "../../../services/auth.service";
import { I18nService } from "../../../services/i18n.service";
import { NotificationService } from "../../../services/notification.service";
import { UtilityService } from "../../../services/utility.service";
import { ActivatedRoute, Router } from "@angular/router";
import { SessionService } from "../../../services/session.service";


@Component({
	selector: "app-login",
	templateUrl: "./login.component.html",
	styleUrls: ["./login.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {
	validateLoginForm: FormGroup;
	passwordVisible = false ;
	tabSetStyle = TAB_SET_STYLE;
	userType: string = "supplier";
	dropDownStyle = AUTH_FORM_DROP_DOWN_STYLE;
	isTenderpilotAdmin: boolean = false;
	returnUrl: string = '';

	listOfOption = [
		{
			label: "global.user_type.buyer",
			value: "buyer",
		},
		{
			label: "global.user_type.supplier",
			value: "supplier",
		},
	];

	constructor(
		private formBuilder: FormBuilder,
		private i18nService: I18nService,
		private authService: AuthService,
		private spinnerService: NgxSpinnerService,
		private notificationService: NotificationService,
		private utilityService: UtilityService,
		private profileService: ProfileService,
		private storageService: StorageService,
		private route: ActivatedRoute,
		private sessionService: SessionService,
        private router: Router
	) {
		this.formBuilder = formBuilder;
		this.i18nService = i18nService;
		this.authService = authService;
		this.spinnerService = spinnerService;
		this.notificationService = notificationService;
		this.utilityService = utilityService;
		this.profileService = profileService;
		this.storageService = storageService;
		this.route = route;
		this.sessionService = sessionService;
	}

	ngOnInit(): void {
		this.returnUrl = this.route.snapshot.queryParams['returnUrl'];

		this.route.queryParams.subscribe((params) => {
			if(params.authToken) {
				this.spinnerService.show();
				this.authService.loginAsAdmin(params.authToken).subscribe( (response) => {
					if (response) {
						this.sessionService.idToken = response.idToken;
						if (this.sessionService.userType === USER_TYPE.BUYER) {
							this.profileService.userRoutes = environment.API_ROUTES.BUYER;
						} else {
							this.profileService.userRoutes = environment.API_ROUTES.SUPPLIER;
						}
						this.utilityService.redirectToDashboardPage();
						this.spinnerService.hide();
						return;
					}
					this.spinnerService.hide();
				});
			}
		});
		this.validateLoginForm = this.formBuilder.group({
			userName: [null, [Validators.required]],
			password: [null, [Validators.required]],
			remember: [false],
			userType: ["supplier", [Validators.required]],
		});
	}

	/**
	 * Function for the submission of a login form.
	 */
	submitLoginForm(templateRef: TemplateRef<{}>, userType): void {

		for (const control in this.validateLoginForm.controls) {
			this.validateLoginForm.controls[control].markAsDirty();
			this.validateLoginForm.controls[control].updateValueAndValidity();
		}
		const data = {
			email: this.validateLoginForm.value.userName,
			password: this.validateLoginForm.value.password,
			rememberMe: this.validateLoginForm.value.remember,
		};

		this.spinnerService.show();
		this.authService.login(data, userType).subscribe(
			(response) => {
				if (response) {
					this.sessionService.idToken = response.idToken;
					if (this.sessionService.userType === USER_TYPE.BUYER) {
						this.profileService.userRoutes = environment.API_ROUTES.BUYER;
						this.utilityService.redirectToDashboardPage();
					} else {
						this.profileService.userRoutes = environment.API_ROUTES.SUPPLIER;
						this.utilityService.redirectToSupplierDashboardPage();
					}
					if( this.returnUrl ) {
						this.router.navigateByUrl(this.returnUrl);
					}
					this.spinnerService.hide();
					return;
				}
				this.spinnerService.hide();
			},
			(error) => {
				this.spinnerService.hide();
				this.notificationService.notificationWithConfirmation(
					templateRef,
					{ message: error?.error?.message }
				);
				if (this.validateLoginForm.value.userName) {
					error.email = this.validateLoginForm.value.userName;
				}
			}
		);
	}

	/**
	 * Switch Login Buttons
	 */
	checkTenderpilotEmail(email: string): void {
		if(email?.indexOf('@tenderpilot.de') !== -1){
			this.isTenderpilotAdmin = true;
		} else {
			this.isTenderpilotAdmin = false;
		}
	}

	/**
	 *	Change language function
	 * @param language : language string
	 */
	changeLanguage(language: string): void {
		this.i18nService.switchLanguage(language);
	}

	/**
	 * Navigate to register/signup page
	 */
	redirectToRegisterPage(): void {
		this.utilityService.navigate(ROUTES.SIGN_UP);
	}

	/**
	 * Navigate to login page
	 */
	redirectToLoginPage(): void {
		this.utilityService.navigate(ROUTES.LOG_IN);
	}

	/**
	 * Navigate to login page
	 */
	redirectToDashboardPage(): void {
		this.utilityService.navigate(
			ROUTES.BUYER_ROUTE + "/" + ROUTES.OVERVIEW
		);
	}

	/**
	 * Handle form reset event
	 */
	formReset(): void {
		this.validateLoginForm.reset({
			userType: this.userType,
			remember: false,
		});
		return;
	}
}
